// Web App Operations Class
// -------------------------
//
//
// Class imports
import { getScoreForPhase } from "@/utils/webAppOperations";
import { EMPTY_STRING, REMEDIATION_IN_PROGRESS_PHASE_VALUE } from "../constants/definitions";
import WebApplicationClass from "./webApplication";

export class WebAppOperationsClass {
  // Get Unverified Apps List
  // -------------------------
  //
  getUnverifiedAppsList(
    listOfWebApps: WebApplicationClass[],
    owner: string = EMPTY_STRING
  ): WebApplicationClass[] {
    const listToReturn: WebApplicationClass[] = [];

    if (listOfWebApps) {
      // Loop through all web apps
      for (let i = 0; i < listOfWebApps.length; i++) {
        // Get a single web app
        const webapp = listOfWebApps[i];
        // If the web app is not verified then add it to the list
        if (
          !webapp.ownershipVerified &&
          (EMPTY_STRING === owner || webapp.owner === owner)
        ) {
          listToReturn.push(webapp);
        }
      }
    }

    return listToReturn;
  }

  // Sort By Highest Risk
  // ---------------------
  // Sorts web applications by the highest risk
  //
  sortHighestRisk(listOfWebApps: WebApplicationClass[]): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    return webapps.sort(this.sortByHighRisk);
  }

  // Sort By Lowest Risk
  // --------------------
  //
  sortLowestRisk(listOfWebApps: WebApplicationClass[]): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    webapps.sort(this.sortByHighRisk);
    return webapps.reverse();
  }

  // Sort By Newest Test First
  // --------------------------
  //
  sortByYoungestTest(
    listOfWebApps: WebApplicationClass[]
  ): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    return webapps.sort(this.sortByMostRecentTest);
  }

  // Sort By Oldest Test First
  // ---------------------------
  //
  sortByOldestTest(
    listOfWebApps: WebApplicationClass[]
  ): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    webapps.sort(this.sortByMostRecentTest);
    return webapps.reverse();
  }

  // Sort By Stage
  // --------------
  //
  sortByStage(listOfWebApps: WebApplicationClass[]): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    return webapps.sort(this.sortByStageOfTest);
  }

  // Sort By Web App Name
  // ----------------------
  //
  sortByWebappName(
    listOfWebApps: WebApplicationClass[]
  ): WebApplicationClass[] {
    // Copy the array so we dont overwrite the old one
    const webapps = listOfWebApps.slice();
    return webapps.sort(function (
      webapp1: WebApplicationClass,
      webapp2: WebApplicationClass
    ) {
      return webapp1.name
        .toLowerCase()
        .localeCompare(webapp2.name.toLowerCase());
    });
  }

  // Get Apps By Username
  // ----------------------
  //
  getAppNamesByOwnerUsername(
    listOfWebApps: WebApplicationClass[],
    username: string
  ): string[] {
    const WebAppsOwnedByUser: string[] = [];

    for (let i = 0; i < listOfWebApps.length; i++) {
      const app = listOfWebApps[i];

      if (app.owner === username) {
        WebAppsOwnedByUser.push(app.name);
      }
    }

    return WebAppsOwnedByUser;
  }

  /*********************************/
  /*      HELPER FUNCTIONS         */
  /*********************************/

  // Sort by High Risk
  sortByHighRisk(
    webapp1: WebApplicationClass,
    webapp2: WebApplicationClass
  ): number {
    const webapp1Score = webapp1.webAppTestStatus.riskscore;
    const webapp2Score = webapp2.webAppTestStatus.riskscore;

    if (webapp1Score > webapp2Score) {
      return -1;
    }
    if (webapp1Score < webapp2Score) {
      return 1;
    }
    return 0;
  }

  // Sort by Youngest Test
  sortByMostRecentTest(
    webapp1: WebApplicationClass,
    webapp2: WebApplicationClass
  ): number {
    const webapp1LastTestDate = webapp1.webAppTestStatus.lastTestDate;
    const webapp2LastTestDate = webapp2.webAppTestStatus.lastTestDate;

    if (webapp1LastTestDate > webapp2LastTestDate) {
      return -1;
    }
    if (webapp1LastTestDate < webapp2LastTestDate) {
      return 1;
    }
    return 0;
  }

  // Sort by Stage
  sortByStageOfTest(
    webapp1: WebApplicationClass,
    webapp2: WebApplicationClass
  ): number {
    const webapp1Phase = webapp1.webAppTestStatus.currentPhase;
    const webapp2Phase = webapp2.webAppTestStatus.currentPhase;
    const webapp1PhaseScore = getScoreForPhase(
      webapp1.webAppTestStatus.currentPhase
    );
    const webapp2PhaseScore = getScoreForPhase(
      webapp2.webAppTestStatus.currentPhase
    );

    // If the web apps are in the same phase then we may return zero
    // Except if that is the remediate phase in which case we compare progression
    if (webapp1PhaseScore > webapp2PhaseScore) {
      return -1;
    }
    if (webapp1PhaseScore < webapp2PhaseScore) {
      return 1;
    }
    if (webapp1Phase === webapp2Phase) {
      if (webapp1Phase !== REMEDIATION_IN_PROGRESS_PHASE_VALUE) {
        return 0;
      } else {
        if (
          webapp1.webAppTestStatus.percentComplete >
          webapp2.webAppTestStatus.percentComplete
        ) {
          return -1;
        }
        if (
          webapp1.webAppTestStatus.percentComplete <
          webapp2.webAppTestStatus.percentComplete
        ) {
          return 1;
        }
        return 0;
      }
    }
    return 0; // Default return for TypeScript
  }
}

export default WebAppOperationsClass;
